import { Box, BoxProps } from '@mui/material'
import { BRAND_MARKS } from 'src/design-system/brand.generated'

// ----------------------------------------------------------------------
// The Tesla mark. A third-party trademark, and not an icon.
//
// It cannot go through `Iconify`: that wrapper strokes a `fill: none` path
// and scales the stroke by an optical step, which on a filled mark draws
// nothing at all. The geometry is never altered, rotated or restyled.
//
// Its ink is always full contrast. `currentColor` is how it sits correctly
// on the brand red and on a dark button, and it is also how it goes wrong:
// inherited from a container authored for muted icons it renders in
// placeholder grey, which measured 2.2:1 on the tile the design uses to name
// a connected account. So the default is the page's own ink rather than
// whatever is being inherited, and a caller that wants it on a fill says so.
//
// Used where the thing being named *is* Tesla: the sign-in button, a
// connected Tesla account, and the connect and reconnect actions. Never for a
// vehicle. A car sourced from Tesla is still a car and takes the `vehicle`
// glyph; swapping it for the brand mark would make the fleet list read as a
// list of accounts.
// ----------------------------------------------------------------------

export interface TeslaMarkProps
  extends Omit<
    BoxProps,
    'children' | 'width' | 'height' | 'dangerouslySetInnerHTML'
  > {
  /** Rendered size in px. The mark is square. */
  size?: number
  /**
   * What the mark says, when nothing beside it already says it. Left off it
   * is decorative and hidden, which is right almost everywhere it appears:
   * a mark labelled `Tesla` inside a button reading `Continue with Tesla`
   * makes the button's accessible name "Tesla Continue with Tesla".
   */
  title?: string
  /**
   * Take the ink of whatever is behind it, for a mark drawn on a fill that
   * already states its own contrast: the leading mark on the Tesla sign-in
   * button, which is white on the brand red. Left off, the mark takes the
   * page's primary ink rather than the container's, so it cannot be handed
   * a muted grey by a tile authored for icons.
   */
  inherit?: boolean
}

/** Beside a button label, matched to the label's cap height. */
export const TESLA_MARK_INLINE = 16

export default function TeslaMark({
  size = TESLA_MARK_INLINE,
  inherit = false,
  title,
  sx,
  ...other
}: TeslaMarkProps & {
  width?: never
  height?: never
  dangerouslySetInnerHTML?: never
}) {
  const mark = BRAND_MARKS['tesla-mark']

  return (
    <Box
      component="svg"
      viewBox={mark.viewBox}
      width={size}
      height={size}
      fill="currentColor"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
      sx={[
        {
          display: 'block',
          flex: '0 0 auto',
          ...(inherit ? {} : { color: 'text.primary' }),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      dangerouslySetInnerHTML={{ __html: mark.body }}
      {...other}
    />
  )
}
